import type {ContentRelationshipField, PrismicDocumentHeader} from '@prismicio/client'
import {isFilled} from '@prismicio/client'

import type {InvoiceMasterDataData, PhishingEmailData, Question, QuestionType} from './types'

type FraudGameQuestionOption = {
  option_text: string | null
}

export type FraudGameQuestionDocumentData = {
  question_type: string | null
  phase: string | null
  question_group: string | null
  short_label: string | null
  timer_seconds: number | null
  points: number | null
  question: string | null
  correct_value: string | null
  options?: FraudGameQuestionOption[] | null
  fermi_range_min?: number | null
  fermi_range_max?: number | null
  fermi_input_placeholder?: string | null
  fermi_range_suffix?: string | null
  phishing_sender_name?: string | null
  phishing_sender_address?: string | null
  phishing_reply_to?: string | null
  phishing_subject?: string | null
  phishing_body?: string | null
  phishing_correct_field_id?: string | null
  invoice_supplier_name?: string | null
  invoice_address?: string | null
  invoice_vat_id?: string | null
  invoice_iban?: string | null
  invoice_number?: string | null
  invoice_date?: string | null
  invoice_description?: string | null
  invoice_amount?: string | null
  master_supplier_name?: string | null
  master_address?: string | null
  master_vat_id?: string | null
  master_iban?: string | null
  invoice_correct_field_id?: string | null
  moss_insight_body?: string | null
  moss_insight_how_moss_helps?: string | null
}

export type FraudGameQuestionDocument = PrismicDocumentHeader<'fraud_game_question'> & {
  uid: string
  data: FraudGameQuestionDocumentData
}

export type FraudGameSliceItem = {
  question: ContentRelationshipField<'fraud_game_question'>
}

const QUESTION_TYPES = new Set<QuestionType>([
  'true-false',
  'multiple-choice',
  'fermi',
  'spot-the-scam',
  'phishing-email',
  'invoice-master-data',
])

const REQUIRED_GROUP_COUNTS: Record<string, number> = {
  'p1-true-false': 1,
  'p1-fermi': 1,
  'p1-scenario-mcq': 1,
  'p1-knowledge-mcq': 1,
  'q14-phishing-email': 2,
  'q13-invoice-master-data': 2,
}

export const getFraudGameSliceItems = (items: unknown[] | undefined): FraudGameSliceItem[] =>
  Array.isArray(items) ? (items as FraudGameSliceItem[]) : []

export const getFraudGameQuestionIDsFromSliceItems = (items: FraudGameSliceItem[]): string[] =>
  items
    .map((item) => (isFilled.contentRelationship(item.question) ? item.question.id : null))
    .filter((id): id is string => typeof id === 'string')

export const mapFraudGameQuestionDocument = (document: FraudGameQuestionDocument): Question | null => {
  const type = asQuestionType(document.data.question_type)
  const phase = asPhase(document.data.phase)
  const shortLabel = asNonEmptyString(document.data.short_label)
  const question = asNonEmptyString(document.data.question)
  const timerSeconds = asNumber(document.data.timer_seconds)
  const points = asNumber(document.data.points)
  const insightBody = asNonEmptyString(document.data.moss_insight_body)
  const insightHowMossHelps = asNonEmptyString(document.data.moss_insight_how_moss_helps)

  if (!type || !phase || !shortLabel || !question || timerSeconds === null || points === null) {
    return null
  }

  if (!insightBody || !insightHowMossHelps) {
    return null
  }

  const baseQuestion = {
    id: document.uid || document.id,
    type,
    shortLabel,
    timerSeconds,
    points,
    question,
    phase,
    questionGroup: asNonEmptyString(document.data.question_group) ?? undefined,
    mossInsight: {
      body: insightBody,
      howMossHelps: insightHowMossHelps,
    },
  }

  if (type === 'phishing-email') {
    const phishingEmail = getPhishingEmailData(document.data)

    if (!phishingEmail) {
      return null
    }

    return {
      ...baseQuestion,
      options: [],
      correctAnswer: phishingEmail.correctFieldId,
      phishingEmail,
    }
  }

  if (type === 'invoice-master-data') {
    const invoiceMasterData = getInvoiceMasterData(document.data)

    if (!invoiceMasterData) {
      return null
    }

    return {
      ...baseQuestion,
      options: [],
      correctAnswer: invoiceMasterData.correctFieldId,
      invoiceMasterData,
    }
  }

  const correctAnswer = asNonEmptyString(document.data.correct_value)

  if (!correctAnswer) {
    return null
  }

  if (type === 'fermi') {
    const min = asNumber(document.data.fermi_range_min)
    const max = asNumber(document.data.fermi_range_max)

    if (min === null || max === null) {
      return null
    }

    return {
      ...baseQuestion,
      options: [],
      correctAnswer,
      fermiRange: [min, max],
      fermiInputPlaceholder: asNonEmptyString(document.data.fermi_input_placeholder) ?? 'Enter a number',
      fermiRangeSuffix: document.data.fermi_range_suffix?.trim() ? document.data.fermi_range_suffix : undefined,
    }
  }

  const options = (document.data.options ?? [])
    .map((option) => option.option_text?.trim() || null)
    .filter((option): option is string => option !== null)

  if (options.length === 0) {
    return null
  }

  return {
    ...baseQuestion,
    options,
    correctAnswer,
  }
}

export const resolveFraudGameQuestionPool = (
  items: FraudGameSliceItem[],
  documents: FraudGameQuestionDocument[] | undefined,
): Question[] | null => {
  if (!documents || documents.length === 0 || items.length === 0) {
    return null
  }

  const documentsByID = new Map(documents.map((document) => [document.id, document]))

  const orderedQuestions = items
    .map((item) => {
      if (!isFilled.contentRelationship(item.question)) {
        return null
      }

      const document = documentsByID.get(item.question.id)

      return document ? mapFraudGameQuestionDocument(document) : null
    })
    .filter((question): question is Question => question !== null)

  return isValidFraudGamePool(orderedQuestions) ? orderedQuestions : null
}

export const isValidFraudGamePool = (pool: Question[]): boolean =>
  Object.entries(REQUIRED_GROUP_COUNTS).every(
    ([group, count]) => pool.filter((question) => question.questionGroup === group).length >= count,
  )

const getPhishingEmailData = (data: FraudGameQuestionDocumentData): PhishingEmailData | null => {
  const senderName = asNonEmptyString(data.phishing_sender_name)
  const senderAddress = asNonEmptyString(data.phishing_sender_address)
  const replyTo = asNonEmptyString(data.phishing_reply_to)
  const subject = asNonEmptyString(data.phishing_subject)
  const body = asNonEmptyString(data.phishing_body)
  const correctFieldId = asNonEmptyString(data.phishing_correct_field_id)

  if (!senderName || !senderAddress || !replyTo || !subject || !body || !correctFieldId) {
    return null
  }

  return {
    senderName,
    senderAddress,
    replyTo,
    subject,
    body,
    correctFieldId,
  }
}

const getInvoiceMasterData = (data: FraudGameQuestionDocumentData): InvoiceMasterDataData | null => {
  const invoiceSupplierName = asNonEmptyString(data.invoice_supplier_name)
  const invoiceAddress = asNonEmptyString(data.invoice_address)
  const invoiceVatId = asNonEmptyString(data.invoice_vat_id)
  const invoiceIban = asNonEmptyString(data.invoice_iban)
  const invoiceNumber = asNonEmptyString(data.invoice_number)
  const invoiceDate = asNonEmptyString(data.invoice_date)
  const invoiceDescription = asNonEmptyString(data.invoice_description)
  const invoiceAmount = asNonEmptyString(data.invoice_amount)
  const masterSupplierName = asNonEmptyString(data.master_supplier_name)
  const masterAddress = asNonEmptyString(data.master_address)
  const masterVatId = asNonEmptyString(data.master_vat_id)
  const masterIban = asNonEmptyString(data.master_iban)
  const correctFieldId = asNonEmptyString(data.invoice_correct_field_id)

  if (
    !invoiceSupplierName ||
    !invoiceAddress ||
    !invoiceVatId ||
    !invoiceIban ||
    !invoiceNumber ||
    !invoiceDate ||
    !invoiceDescription ||
    !invoiceAmount ||
    !masterSupplierName ||
    !masterAddress ||
    !masterVatId ||
    !masterIban ||
    !correctFieldId
  ) {
    return null
  }

  return {
    invoice: {
      supplierName: invoiceSupplierName,
      address: invoiceAddress,
      vatId: invoiceVatId,
      iban: invoiceIban,
      invoiceNumber,
      date: invoiceDate,
      description: invoiceDescription,
      amount: invoiceAmount,
    },
    masterData: {
      supplierName: masterSupplierName,
      address: masterAddress,
      vatId: masterVatId,
      iban: masterIban,
    },
    correctFieldId,
  }
}

const asQuestionType = (value: string | null): QuestionType | null =>
  value && QUESTION_TYPES.has(value as QuestionType) ? (value as QuestionType) : null

const asPhase = (value: string | null): 1 | 2 | 3 | null => {
  if (value === '1') {
    return 1
  }

  if (value === '2') {
    return 2
  }

  if (value === '3') {
    return 3
  }

  return null
}

const asNonEmptyString = (value: string | null | undefined): string | null => {
  const trimmedValue = value?.trim()
  return trimmedValue ? trimmedValue : null
}

const asNumber = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null
